/**
 * WHAT SOMEBODY AGREED TO, FROZEN.
 *
 * An approved scope is a COPY, not a pointer. It records the sections as they
 * stood at the moment of approval - their ids, their revisions, their lengths
 * and how those lengths were arrived at - so that the scope survives every
 * later edit to the drawing. The v0.2 contract says it in one line: approved
 * scope is never edited in place.
 *
 * The reason is a February phone call. A customer approved a job in September,
 * an estimator redrew the garage in October, and the question is what was
 * agreed. A pointer at the live sections answers "whatever it says now", which
 * is not an answer. A copy answers it, and can also say exactly what has moved
 * since, which is the other half of the same question.
 *
 * NOTHING HERE APPROVES ANYTHING ON ITS OWN. approvalBlockers is the gate, and
 * it is deliberately a list of sentences rather than a boolean, because a
 * person told no wants to know what to go and do.
 *
 * It is also NOT a price. Money is lib/pricing.ts and the quote document, both
 * untouched. This freezes the SCOPE - the places, the lengths and the counts
 * that fall out of them - which is the thing a warehouse reserves against and
 * a crew works from.
 */

import { isVerified, type MeasureSource, type RoofSection } from './sections'
import type { Takeoff } from './materials'

/** One section, as it stood when somebody said yes. */
export interface ApprovedSection {
  /** The same id the live section carries. Never re-minted. */
  id: string
  label: string
  kind: string
  /** The revision this scope describes. A later revision is a difference. */
  geometryRev: number
  feet: number
  source: MeasureSource
  verified: boolean
}

export interface ApprovedScope {
  /** This approval's own id, so a later one does not overwrite it. */
  id: string
  at: string
  by: string
  /** Free text: what was agreed, in the words of whoever agreed it. */
  note: string
  /**
   * Which package was agreed. Absent on a scope frozen before packages
   * existed, which is why every reader treats it as optional rather than
   * assuming the default.
   */
  packageId?: string
  packageName?: string
  sections: ApprovedSection[]
  /** The counts as they stood. Copied, for the same reason the sections are. */
  quantities: { key: string; label: string; qty: number; unit: string }[]
  feet: number
}

let seq = 0
export const newApprovalId = (): string =>
  `apr_${Date.now().toString(36)}${(seq++).toString(36)}${Math.random().toString(36).slice(2, 7)}`

export const approvalsOf = (d: { approvals?: ApprovedScope[] }): ApprovedScope[] =>
  Array.isArray(d.approvals) ? d.approvals : []

/** The one that stands. Approvals are append only, so it is the last one. */
export const currentApproval = (d: { approvals?: ApprovedScope[] }): ApprovedScope | null => {
  const list = approvalsOf(d)
  return list.length ? list[list.length - 1] : null
}

/**
 * WHY THIS CANNOT BE APPROVED YET.
 *
 * Sentences, in the order a person would fix them. The P1 gate is the last
 * one and it is the whole point of the phase: a sample scope cannot be
 * approved while an included length is still a guess. Production policy is
 * configurable and needs owner review - that decision is flagged, not assumed.
 */
export function approvalBlockers(sections: RoofSection[]): string[] {
  const included = sections.filter((s) => !s.excluded)
  const out: string[] = []

  if (included.length === 0) {
    out.push('There are no sections in this job.')
    return out
  }

  const nameless = included.filter((s) => !s.label.trim()).length
  if (nameless > 0) {
    out.push(
      nameless === 1
        ? 'One section has no name, so nobody can tell a crew which one it is.'
        : `${nameless} sections have no name, so nobody can tell a crew which ones they are.`,
    )
  }

  const noLength = included.filter((s) => s.measurement.feet <= 0)
  if (noLength.length > 0) {
    out.push(
      noLength.length === 1
        ? `${noLength[0].label.trim() || 'One section'} has no length on it.`
        : `${noLength.length} sections have no length on them.`,
    )
  }

  const guessed = included.filter((s) => s.measurement.feet > 0 && !isVerified(s.measurement))
  if (guessed.length > 0) {
    const feet = Math.round(guessed.reduce((n, s) => n + s.measurement.feet, 0))
    out.push(`${feet} ft has not been measured at the house.`)
  }

  return out
}

export const canApprove = (sections: RoofSection[]): boolean =>
  approvalBlockers(sections).length === 0

/**
 * Take the copy. Called only after the blockers are empty, and it does not
 * check again - the gate belongs on the way in, where a person can be told
 * why, rather than buried in a function that would just refuse silently.
 */
export function approveScope(
  sections: RoofSection[],
  t: Takeoff,
  who: {
    by: string
    at: string
    note?: string
    id?: string
    packageId?: string
    packageName?: string
  },
): ApprovedScope {
  return {
    id: who.id ?? newApprovalId(),
    at: who.at,
    by: who.by,
    note: who.note ?? '',
    packageId: who.packageId,
    packageName: who.packageName,
    feet: t.feet,
    sections: sections
      .filter((s) => !s.excluded)
      .map((s) => ({
        id: s.id,
        label: s.label,
        kind: s.kind,
        geometryRev: s.geometryRev,
        feet: s.measurement.feet,
        source: s.measurement.source,
        verified: isVerified(s.measurement),
      })),
    quantities: t.lines.map((l) => ({ key: l.key, label: l.label, qty: l.qty, unit: l.unit })),
  }
}

export type DriftKind = 'added' | 'removed' | 'length' | 'geometry' | 'unverified' | 'renamed'

export interface Drift {
  kind: DriftKind
  sectionId: string
  /** One sentence, in the words a person would use on the phone. */
  said: string
}

/**
 * WHAT HAS MOVED SINCE SOMEBODY SAID YES.
 *
 * Keyed on section id throughout, which is exactly why the id is minted once
 * and never derived from a label: renaming the front eave to the porch eave
 * has to read as a rename, not as one section vanishing and another appearing.
 *
 * Reported, never auto-applied. A changed scope is a conversation with the
 * customer, and MAT-03 says a substitution or changed geometry requires an
 * approval impact review.
 */
export function driftFrom(approved: ApprovedScope, sections: RoofSection[]): Drift[] {
  const live = new Map(sections.filter((s) => !s.excluded).map((s) => [s.id, s]))
  const out: Drift[] = []

  for (const a of approved.sections) {
    const s = live.get(a.id)
    const name = a.label.trim() || 'A section'
    if (!s) {
      out.push({
        kind: 'removed',
        sectionId: a.id,
        said: `${name} was approved and is no longer in the job.`,
      })
      continue
    }
    if (s.label.trim() !== a.label.trim()) {
      out.push({
        kind: 'renamed',
        sectionId: a.id,
        said: `${name} is now called ${s.label.trim() || 'nothing'}.`,
      })
    }
    if (s.measurement.feet !== a.feet) {
      out.push({
        kind: 'length',
        sectionId: a.id,
        said: `${name} was ${a.feet} ft and is now ${s.measurement.feet} ft.`,
      })
    }
    if (s.geometryRev !== a.geometryRev) {
      out.push({
        kind: 'geometry',
        sectionId: a.id,
        said: `${name} has been redrawn since it was approved.`,
      })
    }
    if (a.verified && !isVerified(s.measurement)) {
      out.push({
        kind: 'unverified',
        sectionId: a.id,
        said: `${name} was measured at the house when it was approved, and no longer counts as measured.`,
      })
    }
  }

  for (const s of live.values()) {
    if (approved.sections.some((a) => a.id === s.id)) continue
    out.push({
      kind: 'added',
      sectionId: s.id,
      said: `${s.label.trim() || 'A new section'} was added after this was approved.`,
    })
  }

  return out
}

/** One line for a screen that only has room for one. */
export function driftWording(list: Drift[]): string | null {
  if (list.length === 0) return null
  if (list.length === 1) return list[0].said
  return `${list.length} things have changed since this was approved.`
}

export interface PickRow {
  /** The number a crew calls out. Position in the approved list, from 1. */
  n: number
  sectionId: string
  label: string
  kind: string
  /** The cut length, in feet, as approved. */
  feet: number
  bulbs: number
  clips: number
  /** True when this row's length was measured at the house. */
  verified: boolean
}

export interface PickList {
  approvalId: string
  at: string
  rows: PickRow[]
  totals: { feet: number; bulbs: number; clips: number; wire: number }
}

/**
 * WHAT THE WAREHOUSE CUTS, NUMBERED.
 *
 * Built from the APPROVED copy and never from the live sections, which is the
 * whole reason the copy exists. A crew that picked against live geometry would
 * be cutting wire for a roof an estimator edited this morning.
 *
 * Numbers are positions in the approved list and they are for talking - "run
 * four is short" - while the section id underneath them is what anything is
 * actually keyed on. Never the other way round: a number is a label, and a
 * label carries no identity.
 *
 * IT IS NOT A RESERVATION. Reserving against stock needs a catalog, an
 * availability count and an idempotent operation key, none of which exist yet;
 * MAT-03 is explicit that reservations are not loaded or installed stock
 * either. This is the list that a reservation would be made FROM.
 */
export function pickList(
  approved: ApprovedScope,
  bulbsFor: (feet: number) => number,
  wireAllowance: number,
): PickList {
  const rows: PickRow[] = approved.sections.map((a, i) => {
    const bulbs = bulbsFor(a.feet)
    return {
      n: i + 1,
      sectionId: a.id,
      label: a.label.trim() || `Section ${i + 1}`,
      kind: a.kind,
      feet: a.feet,
      bulbs,
      clips: bulbs,
      verified: a.verified,
    }
  })

  const feet = Math.round(rows.reduce((n, r) => n + r.feet, 0) * 100) / 100
  const bulbs = rows.reduce((n, r) => n + r.bulbs, 0)
  return {
    approvalId: approved.id,
    at: approved.at,
    rows,
    totals: { feet, bulbs, clips: bulbs, wire: Math.ceil(feet * wireAllowance) },
  }
}
